import { Component, computed, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import { CompareVcodeService } from 'src/app/services/compare-vcode.service';
import { ErrorSnackbarComponent } from '../../custom/error-snackbar/error-snackbar.component';
import { ResetPasswordDataService } from 'src/app/services/reset-password-data.service';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';
import { NgOptimizedImage } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatRippleModule, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-verification-code',
  templateUrl: './verification-code.component.html',
  styleUrls: ['./verification-code.component.css'],
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgOptimizedImage,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MatDialogModule,
    MatSnackBarModule,
    MatRippleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSnackBarModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatDividerModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatBottomSheetModule,
    MatMenuModule,
    MatBadgeModule
],
})
export class VerificationCodeComponent implements OnInit {
  private analytics = inject(Analytics);
  verificationCodeForm: FormGroup;
  isLightMode = computed(() => this.themeService.isLightMode());

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private snackBar: MatSnackBar,
    private compareVcode: CompareVcodeService,
    private resetPasswordDataService: ResetPasswordDataService,
    private themeService: ThemeService
  ) {
    this.verificationCodeForm = this.fb.group({
      verificationCode: [
        '',
        [Validators.required, Validators.pattern(/^\d{6}$/)],
      ],
    });
  }

  ngOnInit(): void {
    logEvent(this.analytics, LogEventsEnum.auth_forgot_verify_otp);
  }

  onSubmit() {
    if (this.verificationCodeForm.invalid) {
      this.verificationCodeForm.markAllAsTouched();
      return;
    }

    const formValues = this.verificationCodeForm.value;

    const email = this.resetPasswordDataService.getEmail();
    if (!email) {
      this.router.navigate(['/forgot-password']);
      this.snackBar.openFromComponent(ErrorSnackbarComponent, {
        data: {
          message: 'Email not found. Please reenter email',
        },
        duration: 3000,
      });
      return;
    }

    this.compareVcode
      .mutate({
        email: email,
        vcode: formValues.verificationCode,
      })
      .subscribe({
        next: (response) => {
          const verificationCodeResponse = response.data;

          if (verificationCodeResponse.compareVcode === 201) {
            this.resetPasswordDataService.setVcode(formValues.verificationCode);
            this.router.navigate(['/new-password']);
          } else {
            this.snackBar.openFromComponent(ErrorSnackbarComponent, {
              data: {
                message: 'Invalid varification code. Please reenter it again',
              },
              duration: 3000,
            });
          }
        },
        error: (error) => {
          console.error('Error logging in:', error.message);
          this.router.navigate(['/forgot-password']);
          this.snackBar.openFromComponent(ErrorSnackbarComponent, {
            data: {
              message: 'Sending verification code failed. Please reenter email',
            },
            duration: 3000,
          });
        },
      });
  }
}
